import { FC } from "react";
import { BodyDiagram } from "../BodyDiagram";
import styles from "./ProgramInfoBlock.module.scss";

// Импорты иконок
import { ReactComponent as TargetIcon } from "@/assets/img/program-params/target.svg";
import { ReactComponent as StopwatchIcon } from "@/assets/img/program-params/stopwatch.svg";
import { ReactComponent as BarbellIcon } from "@/assets/img/program-params/barbell.svg";
import { ReactComponent as BandageIcon } from "@/assets/img/program-params/bandage.svg";
import { ReactComponent as WalkingIcon } from "@/assets/img/program-params/walking.svg";

type ProgramInfoBlockProps = {
  stages?: string[]; // Список фаз программы
  accent?: number[]; // Массив ID областей внимания (1-9)
  injuries?: string[]; // Массив травм
  trainingDuration?: string; // Длительность тренировок
  quantity?: number; // Количество тренировок
};

export const ProgramInfoBlock: FC<ProgramInfoBlockProps> = ({
  stages = [],
  accent = [],
  injuries = [],
  trainingDuration,
  quantity,
}) => {
  // Функция для получения названия области внимания
  const getFocusAreaName = (areaId: number): string => {
    const areaNames: Record<number, string> = {
      1: "Шея",
      2: "Плечи / Плечевой пояс",
      3: "Руки",
      4: "Спина",
      5: "Грудь",
      6: "Живот",
      7: "Таз / Ягодицы",
      8: "Бедра",
      9: "Голени",
    };
    return areaNames[areaId] || "Неизвестно";
  };

  // Формируем строку с областями внимания
  const accentText = accent.length > 0
    ? accent.map((id) => getFocusAreaName(id)).filter(name => name !== "Неизвестно").join(", ") || "Не указано"
    : "Не указано";

  // Формируем строку с травмами
  const injuriesText = injuries.length > 0
    ? injuries.join(", ")
    : "Нет";

  return (
    <div className={styles.programInfoBlock}>
      <div className={styles.content}>
        {/* Параметры программы */}
        <div className={styles.params}>
          {/* Область внимания */}
          <div className={styles.param}>
            <div className={styles.paramIcon}>
              <TargetIcon />
            </div>
            <div className={styles.paramContent}>
              <span className={styles.paramLabel}>ОБЛАСТЬ ВНИМАНИЯ</span>
              <span className={styles.paramValue}>{accentText}</span>
            </div>
          </div>

          {/* Длительность тренировок */}
          {trainingDuration && (
            <div className={styles.param}>
              <div className={styles.paramIcon}>
                <StopwatchIcon />
              </div>
              <div className={styles.paramContent}>
                <span className={styles.paramLabel}>ДЛИТЕЛЬНОСТЬ ТРЕНИРОВОК</span>
                <span className={styles.paramValue}>{trainingDuration}</span>
              </div>
            </div>
          )}

          {/* Количество */}
          {quantity !== undefined && (
            <div className={styles.param}>
              <div className={styles.paramIcon}>
                <BarbellIcon />
              </div>
              <div className={styles.paramContent}>
                <span className={styles.paramLabel}>КОЛ-ВО ТРЕНИРОВОК</span>
                <span className={styles.paramValue}>{quantity}</span>
              </div>
            </div>
          )}

          {/* Травмы */}
          <div className={styles.param}>
            <div className={styles.paramIcon}>
              <BandageIcon />
            </div>
            <div className={styles.paramContent}>
              <span className={styles.paramLabel}>ТРАВМЫ</span>
              <span className={styles.paramValue}>{injuriesText}</span>
            </div>
          </div>

          {/* Шаги */}
          <div className={styles.param}>
            <div className={styles.paramIcon}>
              <WalkingIcon />
            </div>
            <div className={styles.paramContent}>
              <span className={styles.paramLabel}>ШАГИ</span>
              <span className={styles.paramValue}>10000</span>
            </div>
          </div>
        </div>

        {/* Правая часть - диаграмма тела */}
        <div className={styles.diagram}>
          <BodyDiagram focusAreas={accent} />
        </div>
      </div>

      {/* Фазы программы */}
      {stages.length > 0 && (
        <div className={styles.phasesContainer}>
          <div className={styles.phases}>
            {stages.map((stage, index) => (
              <div key={index} className={styles.phaseWrapper}>
                <div className={styles.phaseRow}>
                  <div className={`${styles.phaseCircle} ${index === 0 ? styles.phaseCircleActive : ""}`}>
                    {index + 1}
                  </div>
                  <div className={styles.phaseContent}>
                    <span className={`${styles.phaseLabel} ${index === 0 ? styles.phaseLabelActive : ""}`}>ФАЗА</span>
                    <span className={`${styles.phaseValue} ${index === 0 ? styles.phaseValueActive : ""}`}>{stage}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
